import { ReactNode, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import QRCode from 'qrcode'
import { Deposit, PaymentInfo } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { tokens } from '../../theme'
import { formatCzk } from './format'

interface PaymentInfoDialogProps {
  open: boolean
  deposit: Deposit | null
  onClose: () => void
}

/**
 * QR platba nezaplaceného vkladu (F2-C): platební údaje si vytáhne z BE
 * (sběrný účet, VS, SPD řetězec) a z SPD vyrenderuje QR kód pro klienta.
 */
export function PaymentInfoDialog({ open, deposit, onClose }: PaymentInfoDialogProps) {
  const api = useOpsApi()

  const [info, setInfo] = useState<PaymentInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !deposit) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setInfo(null)
    setQrDataUrl(null)
    api
      .getPaymentInfo(deposit.id)
      .then((loaded) => {
        if (!cancelled) setInfo(loaded)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(apiError(err, 'Načtení platebních údajů selhalo'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, deposit, api])

  // QR kód z SPD řetězce (vzniká až po načtení platebních údajů).
  useEffect(() => {
    if (!info) return
    let cancelled = false
    QRCode.toDataURL(info.spd, { width: 240, margin: 1 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url)
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [info])

  const infoRow = (label: string, value: ReactNode) => (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ pt: '2px' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>QR platba</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {info && !loading && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 }}
          >
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="QR platba"
                width={240}
                height={240}
                style={{ borderRadius: tokens.radius.sm }}
              />
            ) : (
              <Box
                sx={{
                  width: 240,
                  height: 240,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            <Box
              component="dl"
              sx={{
                m: 0,
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                columnGap: 2,
                rowGap: 1,
              }}
            >
              {infoRow('Účet', info.account)}
              {infoRow('Částka', formatCzk(info.amount))}
              {infoRow('VS', info.variableSymbol)}
              {infoRow('Zpráva', info.message)}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose}>
          Zavřít
        </Button>
      </DialogActions>
    </Dialog>
  )
}
